/**
 * Hierarchy panel projection (S2.3) — PURE, framework-free (no axios/i18n/React
 * import chain), so the node-env vitest suite exercises the EXACT code the
 * panel renders (`resolvePrice`-style split; `useRoleHierarchy` re-exports it).
 *
 * Groups roles by seniority band and derives per-role grant counts through the
 * SAME pure resolver both consumers read (`resolveEffectiveAccess` /
 * `resolveRoleClosure` — shared-selector discipline, plan §Resolver contract).
 * Bands are display/sort ONLY — a reorder can never change a count (S2.3 test).
 */

import { resolveEffectiveAccess, resolveRoleClosure } from '@/lib/rbac';
import type { GovernanceRole, RoleLevel } from './model';

/** One band section of the hierarchy panel (roles grouped for display only). */
export interface HierarchyBand {
  /** The band, or null for the trailing "no level" group. */
  level: RoleLevel | null;
  roles: HierarchyRow[];
}

/** One role row of the hierarchy panel — resolver-derived counts included. */
export interface HierarchyRow {
  role: GovernanceRole;
  /** Names of the roles this role inherits from (direct edges, not closure). */
  inheritsFromNames: string[];
  /** Direct grant-list size (what the matrix edits). */
  directGrantCount: number;
  /**
   * Effective Axis-A grant count — direct ∪ inherited ∪ implied, computed by
   * the shared resolver. Equals `directGrantCount` on a leaf role with no
   * implications.
   */
  effectiveGrantCount: number;
}

/**
 * Group roles by band (band order ascending, then `levelOrder`, then name;
 * band-less roles trail in a `level: null` group) with per-role counts.
 */
export function buildHierarchyRows(
  roles: readonly GovernanceRole[],
  levels: readonly RoleLevel[]
): HierarchyBand[] {
  const nameById = new Map(roles.map((role) => [role.id, role.name]));
  const toRow = (role: GovernanceRole): HierarchyRow => {
    const access = resolveEffectiveAccess({
      // A per-role probe identity: the role held flat, no org scoping — Axis A only.
      identity: { userId: `probe:${role.id}`, roleIds: [role.id], assignments: [] },
      roles,
      orgs: [],
    });
    return {
      role,
      inheritsFromNames: (role.inheritsFrom ?? [])
        .map((id) => nameById.get(id))
        .filter((name): name is string => Boolean(name)),
      directGrantCount: role.permissions.length,
      effectiveGrantCount: access.grants.size,
    };
  };

  const sortRoles = (list: GovernanceRole[]): HierarchyRow[] =>
    list
      .sort(
        (a, b) =>
          (a.levelOrder ?? Number.MAX_SAFE_INTEGER) - (b.levelOrder ?? Number.MAX_SAFE_INTEGER) ||
          a.name.localeCompare(b.name)
      )
      .map(toRow);

  const bands: HierarchyBand[] = [...levels]
    .sort((a, b) => a.order - b.order)
    .map((level) => ({
      level,
      roles: sortRoles(roles.filter((role) => role.levelId === level.id)),
    }));
  const bandIds = new Set(levels.map((level) => level.id));
  const unbanded = roles.filter((role) => !role.levelId || !bandIds.has(role.levelId));
  if (unbanded.length > 0) bands.push({ level: null, roles: sortRoles(unbanded) });
  return bands.filter((band) => band.roles.length > 0);
}

/**
 * The closure a role would resolve with (self excluded) — the inheritance
 * editor's reach hint; the acyclic pre-check itself runs `findRoleCycle`
 * (the resolver's own cycle definition) at the call site.
 */
export function roleClosureNames(role: GovernanceRole, roles: readonly GovernanceRole[]): string[] {
  const nameById = new Map(roles.map((candidate) => [candidate.id, candidate.name]));
  return [...resolveRoleClosure([role.id], roles)]
    .filter((id) => id !== role.id)
    .map((id) => nameById.get(id))
    .filter((name): name is string => Boolean(name));
}
